import {createReducer, on} from '@ngrx/store';
import {ProductManagementErrorInterface} from '../../../error/error-handling/product-management-error';
import {defaultProductQueryParams, ProductQueryParams, RegistrationSummary} from '../../../models';
import {Bundle, Catalog, CollectionMetadata, Product} from '../../../models/clare';
import {CatalogDetailActions} from './catalog-detail.actions';

export const CATALOG_DETAIL_STATE_KEY = 'catalogDetail';

export type CatalogDetailUpdateStatus = 'idle' | 'loading' | 'success' | 'failure';
export type CatalogProductSelectionSaveStatus = 'idle' | 'loading' | 'success' | 'failure';
export type CatalogBundleSelectionSaveStatus = CatalogProductSelectionSaveStatus;

export interface CatalogProductsState {
  products: Product[];
  paginationMeta: CollectionMetadata;
  query: ProductQueryParams;
  loading: boolean;
  error: ProductManagementErrorInterface | undefined;
  selectedProductIds: string[];
}

export interface CatalogBundlesState {
  bundles: Bundle[];
  paginationMeta: CollectionMetadata;
  query: ProductQueryParams;
  loaded: boolean;
  loading: boolean;
  error: ProductManagementErrorInterface | undefined;
  selectedBundleIds: string[];
}

export interface CatalogProductSelectionState {
  products: Product[];
  paginationMeta: CollectionMetadata;
  query: ProductQueryParams;
  loading: boolean;
  error: ProductManagementErrorInterface | undefined;
  selectedProductIds: string[];
  saveStatus: CatalogProductSelectionSaveStatus;
  saveError: ProductManagementErrorInterface | undefined;
}

export interface CatalogBundleSelectionState {
  bundles: Bundle[];
  paginationMeta: CollectionMetadata;
  query: ProductQueryParams;
  loading: boolean;
  error: ProductManagementErrorInterface | undefined;
  selectedBundleIds: string[];
  saveStatus: CatalogBundleSelectionSaveStatus;
  saveError: ProductManagementErrorInterface | undefined;
}

export interface CatalogDetailState {
  catalog: Catalog | undefined;
  loading: boolean;
  error: ProductManagementErrorInterface | undefined;
  registrations: RegistrationSummary[];
  registrationsLoading: boolean;
  registrationsError: ProductManagementErrorInterface | undefined;
  updateStatus: CatalogDetailUpdateStatus;
  updateError: ProductManagementErrorInterface | undefined;
  products: CatalogProductsState;
  bundles: CatalogBundlesState;
  productSelection: CatalogProductSelectionState;
  bundleSelection: CatalogBundleSelectionState;
}

export const initialCatalogProductSelectionState: CatalogProductSelectionState = {
  products: [],
  paginationMeta: {result_type: 'Items::Product', pagination: {}} as CollectionMetadata,
  query: {...defaultProductQueryParams(), take: 25},
  loading: false,
  error: undefined,
  selectedProductIds: [],
  saveStatus: 'idle',
  saveError: undefined,
};

export const initialCatalogBundleSelectionState: CatalogBundleSelectionState = {
  bundles: [],
  paginationMeta: {result_type: 'Items::Bundle', pagination: {}} as CollectionMetadata,
  query: {...defaultProductQueryParams(), take: 25},
  loading: false,
  error: undefined,
  selectedBundleIds: [],
  saveStatus: 'idle',
  saveError: undefined,
};

export const initialCatalogDetailState: CatalogDetailState = {
  catalog: undefined,
  loading: false,
  error: undefined,
  registrations: [],
  registrationsLoading: false,
  registrationsError: undefined,
  updateStatus: 'idle',
  updateError: undefined,
  products: {
    products: [],
    paginationMeta: {result_type: 'Items::Product', pagination: {}} as CollectionMetadata,
    query: defaultProductQueryParams(),
    loading: false,
    error: undefined,
    selectedProductIds: [],
  },
  bundles: {
    bundles: [],
    paginationMeta: {result_type: 'Items::Bundle', pagination: {}} as CollectionMetadata,
    query: defaultProductQueryParams(),
    loaded: false,
    loading: false,
    error: undefined,
    selectedBundleIds: [],
  },
  productSelection: initialCatalogProductSelectionState,
  bundleSelection: initialCatalogBundleSelectionState,
};

export const catalogDetailReducer = createReducer(
  initialCatalogDetailState,
  on(CatalogDetailActions.load, (): CatalogDetailState => ({
    ...initialCatalogDetailState,
    loading: true,
  })),
  on(CatalogDetailActions.loadSuccess, (state, {catalog}): CatalogDetailState => ({
    ...state,
    catalog,
    loading: false,
    error: undefined,
    updateStatus: 'idle',
    updateError: undefined,
  })),
  on(CatalogDetailActions.loadRegistrations, (state): CatalogDetailState => ({
    ...state,
    registrationsLoading: true,
    registrationsError: undefined,
  })),
  on(CatalogDetailActions.loadRegistrationsSuccess, (state, {registrations}): CatalogDetailState => ({
    ...state,
    registrations: [...registrations],
    registrationsLoading: false,
    registrationsError: undefined,
  })),
  on(CatalogDetailActions.loadRegistrationsFailure, (state, {error}): CatalogDetailState => ({
    ...state,
    registrations: [],
    registrationsLoading: false,
    registrationsError: error,
  })),
  on(CatalogDetailActions.loadProducts, (state): CatalogDetailState => ({
    ...state,
    products: {...state.products, loading: true, error: undefined},
  })),
  on(CatalogDetailActions.productsQueryChanged, (state, {queryParams}): CatalogDetailState => ({
    ...state,
    products: {
      ...state.products,
      query: {...state.products.query, ...queryParams},
      loading: true,
      error: undefined,
    },
  })),
  on(CatalogDetailActions.loadProductsSuccess, (state, {products}): CatalogDetailState => ({
    ...state,
    products: {
      ...state.products,
      products: [...products.result],
      paginationMeta: products.metadata,
      loading: false,
      error: undefined,
    },
  })),
  on(CatalogDetailActions.loadProductsFailure, (state, {error}): CatalogDetailState => ({
    ...state,
    products: {...state.products, loading: false, error},
  })),
  on(
    CatalogDetailActions.productSelectionChanged,
    (state, {currentPageProductIds, selectedProductIds}): CatalogDetailState => ({
      ...state,
      products: {
        ...state.products,
        selectedProductIds: [
          ...state.products.selectedProductIds.filter(productId => !currentPageProductIds.includes(productId)),
          ...selectedProductIds,
        ],
      },
    })
  ),
  on(CatalogDetailActions.removeProductsSuccess, (state, {productIds}): CatalogDetailState => {
    const removedProductIds = new Set(productIds.map(String));

    return {
      ...state,
      products: {
        ...state.products,
        selectedProductIds: state.products.selectedProductIds.filter(productId => !removedProductIds.has(productId)),
      },
    };
  }),
  on(CatalogDetailActions.loadBundles, (state): CatalogDetailState => ({
    ...state,
    bundles: {...state.bundles, loading: true, error: undefined},
  })),
  on(CatalogDetailActions.bundlesQueryChanged, (state, {queryParams}): CatalogDetailState => ({
    ...state,
    bundles: {
      ...state.bundles,
      query: {...state.bundles.query, ...queryParams},
      loading: true,
      error: undefined,
    },
  })),
  on(CatalogDetailActions.loadBundlesSuccess, (state, {bundles}): CatalogDetailState => ({
    ...state,
    bundles: {
      ...state.bundles,
      bundles: [...bundles.result],
      paginationMeta: bundles.metadata,
      loaded: true,
      loading: false,
      error: undefined,
    },
  })),
  on(CatalogDetailActions.loadBundlesFailure, (state, {error}): CatalogDetailState => ({
    ...state,
    bundles: {...state.bundles, loading: false, error},
  })),
  on(
    CatalogDetailActions.bundleSelectionChanged,
    (state, {currentPageBundleIds, selectedBundleIds}): CatalogDetailState => ({
      ...state,
      bundles: {
        ...state.bundles,
        selectedBundleIds: [
          ...state.bundles.selectedBundleIds.filter(bundleId => !currentPageBundleIds.includes(bundleId)),
          ...selectedBundleIds,
        ],
      },
    })
  ),
  on(CatalogDetailActions.removeBundlesSuccess, (state, {bundleIds}): CatalogDetailState => {
    const removedBundleIds = new Set(bundleIds.map(String));

    return {
      ...state,
      bundles: {
        ...state.bundles,
        selectedBundleIds: state.bundles.selectedBundleIds.filter(bundleId => !removedBundleIds.has(bundleId)),
      },
    };
  }),
  on(CatalogDetailActions.openProductChooser, (state): CatalogDetailState => ({
    ...state,
    productSelection: initialCatalogProductSelectionState,
  })),
  on(CatalogDetailActions.closeProductChooser, (state): CatalogDetailState => ({
    ...state,
    productSelection: initialCatalogProductSelectionState,
  })),
  on(CatalogDetailActions.loadProductChoices, (state): CatalogDetailState => ({
    ...state,
    productSelection: {...state.productSelection, loading: true, error: undefined},
  })),
  on(CatalogDetailActions.productChoicesQueryChanged, (state, {queryParams}): CatalogDetailState => ({
    ...state,
    productSelection: {
      ...state.productSelection,
      query: {...state.productSelection.query, ...queryParams},
      loading: true,
      error: undefined,
    },
  })),
  on(CatalogDetailActions.loadProductChoicesSuccess, (state, {products}): CatalogDetailState => ({
    ...state,
    productSelection: {
      ...state.productSelection,
      products: [...products.result],
      paginationMeta: products.metadata,
      loading: false,
      error: undefined,
    },
  })),
  on(CatalogDetailActions.loadProductChoicesFailure, (state, {error}): CatalogDetailState => ({
    ...state,
    productSelection: {...state.productSelection, loading: false, error},
  })),
  on(
    CatalogDetailActions.productChoicesSelectionChanged,
    (state, {currentPageProductIds, selectedProductIds}): CatalogDetailState => ({
      ...state,
      productSelection: {
        ...state.productSelection,
        selectedProductIds: [
          ...state.productSelection.selectedProductIds.filter(productId => !currentPageProductIds.includes(productId)),
          ...selectedProductIds,
        ],
      },
    })
  ),
  on(CatalogDetailActions.addSelectedProducts, (state): CatalogDetailState => ({
    ...state,
    productSelection: {...state.productSelection, saveStatus: 'loading', saveError: undefined},
  })),
  on(CatalogDetailActions.addSelectedProductsSuccess, (state): CatalogDetailState => ({
    ...state,
    productSelection: {...state.productSelection, saveStatus: 'success', saveError: undefined},
  })),
  on(CatalogDetailActions.addSelectedProductsFailure, (state, {error}): CatalogDetailState => ({
    ...state,
    productSelection: {...state.productSelection, saveStatus: 'failure', saveError: error},
  })),
  on(CatalogDetailActions.openBundleChooser, CatalogDetailActions.closeBundleChooser, (state): CatalogDetailState => ({
    ...state,
    bundleSelection: initialCatalogBundleSelectionState,
  })),
  on(CatalogDetailActions.loadBundleChoices, (state): CatalogDetailState => ({
    ...state,
    bundleSelection: {...state.bundleSelection, loading: true, error: undefined},
  })),
  on(CatalogDetailActions.bundleChoicesQueryChanged, (state, {queryParams}): CatalogDetailState => ({
    ...state,
    bundleSelection: {
      ...state.bundleSelection,
      query: {...state.bundleSelection.query, ...queryParams},
      loading: true,
      error: undefined,
    },
  })),
  on(CatalogDetailActions.loadBundleChoicesSuccess, (state, {bundles}): CatalogDetailState => ({
    ...state,
    bundleSelection: {
      ...state.bundleSelection,
      bundles: [...bundles.result],
      paginationMeta: bundles.metadata,
      loading: false,
      error: undefined,
    },
  })),
  on(CatalogDetailActions.loadBundleChoicesFailure, (state, {error}): CatalogDetailState => ({
    ...state,
    bundleSelection: {...state.bundleSelection, loading: false, error},
  })),
  on(
    CatalogDetailActions.bundleChoicesSelectionChanged,
    (state, {currentPageBundleIds, selectedBundleIds}): CatalogDetailState => ({
      ...state,
      bundleSelection: {
        ...state.bundleSelection,
        selectedBundleIds: [
          ...state.bundleSelection.selectedBundleIds.filter(bundleId => !currentPageBundleIds.includes(bundleId)),
          ...selectedBundleIds,
        ],
      },
    })
  ),
  on(CatalogDetailActions.addSelectedBundles, (state): CatalogDetailState => ({
    ...state,
    bundleSelection: {...state.bundleSelection, saveStatus: 'loading', saveError: undefined},
  })),
  on(CatalogDetailActions.addSelectedBundlesSuccess, (state): CatalogDetailState => ({
    ...state,
    bundleSelection: {...state.bundleSelection, saveStatus: 'success', saveError: undefined},
  })),
  on(CatalogDetailActions.addSelectedBundlesFailure, (state, {error}): CatalogDetailState => ({
    ...state,
    bundleSelection: {...state.bundleSelection, saveStatus: 'failure', saveError: error},
  })),
  on(CatalogDetailActions.loadFailure, (state, {error}): CatalogDetailState => ({
    ...state,
    catalog: undefined,
    loading: false,
    error,
    updateStatus: 'idle',
    updateError: undefined,
  })),
  on(CatalogDetailActions.update, (state): CatalogDetailState => ({
    ...state,
    updateStatus: 'loading',
    updateError: undefined,
  })),
  on(CatalogDetailActions.updateSuccess, (state, {catalog}): CatalogDetailState => ({
    ...state,
    catalog,
    updateStatus: 'success',
    updateError: undefined,
  })),
  on(CatalogDetailActions.updateFailure, (state, {error}): CatalogDetailState => ({
    ...state,
    updateStatus: 'failure',
    updateError: error,
  })),
  on(CatalogDetailActions.resetUpdateStatus, (state): CatalogDetailState => ({
    ...state,
    updateStatus: 'idle',
    updateError: undefined,
  }))
);
